// @generated by web/tools/codegen — DO NOT EDIT

import { routes, type Route } from '@letsgethome/routes';

import type { MessageKeys } from './messages_gen';

/** A tab of `manifests/routes.yaml` `navigation.tabs[]`, by `name:`. */
export type MobileTabName = 'home' | 'saved' | 'messages' | 'profile';

/**
 * The CLOSED icon vocabulary (`routes_model::NavTabIcon::ALL`). The tab bar
 * maps it to a glyph with an exhaustive switch.
 */
export type MobileTabIcon = 'search' | 'heart' | 'message' | 'user';

export interface MobileTab {
  readonly name: MobileTabName;
  /** The ONE `tab.*` ui-strings key, shared with iOS and Android. */
  readonly labelKey: MessageKeys<''>;
  readonly icon: MobileTabIcon;
  /** The `web:` twin's `public_routes` id. */
  readonly routeId: string;
  /** The twin's full path — what `href()` must address. */
  readonly path: string;
  /** The twin's `@letsgethome/routes` builder (`web_builder:`). */
  readonly href: () => Route;
}

/** The tab bar, in `navigation.tabs[]` order (left to right). */
export const MOBILE_TABS: readonly MobileTab[] = [
  { name: 'home', labelKey: 'tab.explore', icon: 'search', routeId: 'marketing/home', path: '/', href: () => routes.home() },
  { name: 'saved', labelKey: 'tab.saved', icon: 'heart', routeId: 'account/saved', path: '/saved', href: () => routes.account.saved() },
  { name: 'messages', labelKey: 'tab.messages', icon: 'message', routeId: 'conversations/index', path: '/conversations', href: () => routes.account.conversations.index() },
  { name: 'profile', labelKey: 'tab.profile', icon: 'user', routeId: 'account/profile', path: '/profile', href: () => routes.account.profile() },
];

/**
 * `route id → tab`, DERIVED from the route tree: a route belongs to the tab
 * whose twin's full path is its longest segment-boundary path prefix (`/`
 * only of itself), and a listings browse surface to the `web_browse` tab.
 */
export const MOBILE_TAB_BY_ROUTE_ID: Readonly<Record<string, MobileTabName>> = {
  'account/profile': 'profile',
  'account/saved': 'saved',
  'conversations/index': 'messages',
  'conversations/thread': 'messages',
  'district-detail/buy': 'home',
  'district-detail/rent': 'home',
  'listings/buy': 'home',
  'listings/buy-type': 'home',
  'listings/rent': 'home',
  'listings/rent-type': 'home',
  'marketing/home': 'home',
};

/** The active tab for the page whose LEAF route match is `routeId`. */
export function activeMobileTab(routeId: string | undefined): MobileTabName | null {
  if (routeId === undefined) return null;
  return MOBILE_TAB_BY_ROUTE_ID[routeId] ?? null;
}

/**
 * How a screen meets the edges (`routes_model::ScreenFrame`, mobile ADR 019) —
 * the same closed vocabulary iOS and Android switch on.
 */
export type ScreenFrame = 'list' | 'form' | 'canvas' | 'canvas_bleed_top' | 'map_with_sheet';

/** What sits at a screen's bottom edge (`routes_model::BottomRole`). */
export type BottomRole = 'none' | 'pinned_bar' | 'composer' | 'tab_bar';

/** How a screen is entered and left (`routes_model::Presentation`). */
export type Presentation = 'push' | 'flow' | 'task';

export interface ScreenSpec {
  /** The `navigation:` row's `name:` — the same screen on every surface. */
  readonly name: string;
  readonly frame: ScreenFrame;
  readonly bottom: BottomRole;
  readonly presentation: Presentation;
}

/**
 * `web twin route id → screen spec`, for every navigation row with a `web:`
 * twin — resolved by `routes_model::resolve_screen_frame`, the resolution the
 * phones' kernel emits from. No second copy: this file IS the web's table.
 */
export const SCREEN_SPEC_BY_ROUTE_ID: Readonly<Record<string, ScreenSpec>> = {
  'account/listings': { name: 'my_listings', frame: 'list', bottom: 'none', presentation: 'push' },
  'account/profile': { name: 'profile', frame: 'list', bottom: 'tab_bar', presentation: 'push' },
  'account/saved': { name: 'saved', frame: 'list', bottom: 'tab_bar', presentation: 'push' },
  'conversations/index': { name: 'messages', frame: 'list', bottom: 'tab_bar', presentation: 'push' },
  'conversations/thread': { name: 'conversation', frame: 'list', bottom: 'composer', presentation: 'push' },
  'listing-detail': { name: 'listing_show', frame: 'canvas_bleed_top', bottom: 'pinned_bar', presentation: 'push' },
  'listing-detail/inquiry': { name: 'inquiry', frame: 'form', bottom: 'pinned_bar', presentation: 'task' },
  'marketing/home': { name: 'home', frame: 'canvas_bleed_top', bottom: 'tab_bar', presentation: 'push' },
  'marketing/licenses': { name: 'licenses', frame: 'list', bottom: 'none', presentation: 'push' },
  'wizard/new-listing': { name: 'listing_wizard', frame: 'form', bottom: 'pinned_bar', presentation: 'flow' },
};

/** The screen spec of the page whose LEAF route match is `routeId`, if it is a navigation screen. */
export function screenSpec(routeId: string | undefined): ScreenSpec | null {
  if (routeId === undefined) return null;
  return SCREEN_SPEC_BY_ROUTE_ID[routeId] ?? null;
}
